import type { SVGProps } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { useMatchMedia } from '@xing-com/crate-hooks-use-match-media';
import { mediaConfined } from '@xing-com/layout-tokens';
import { motionEasingStandard, motionTimeXXS, spaceS } from '@xing-com/tokens';
import { BodyCopy } from '@xing-com/typography';

type Illustration = (props: SVGProps<SVGSVGElement>) => JSX.Element;

export type FeedbackVariant = 'like' | 'neutral' | 'dislike';

type Props = {
  Illustration: Illustration;
  variant: FeedbackVariant;
  onClick: (feedback: FeedbackVariant) => void;
};

const A11Y_LABEL: Record<FeedbackVariant, string> = {
  like: 'JOBS_SEARCH_AI_FEEDBACK_LIKE_A11Y_LABEL',
  neutral: 'JOBS_SEARCH_AI_FEEDBACK_NEUTRAL_A11Y_LABEL',
  dislike: 'JOBS_SEARCH_AI_FEEDBACK_DISLIKE_A11Y_LABEL',
};

export const ColorFeedback: React.FC<Props> = ({
  Illustration,
  variant,
  onClick,
}) => {
  const { formatMessage } = useIntl();
  const isBigScreen = useMatchMedia(mediaConfined);

  const handleOnClick = (): void => {
    onClick(variant);
  };

  return (
    <Button
      aria-label={
        isBigScreen ? undefined : formatMessage({ id: A11Y_LABEL[variant] })
      }
      onClick={handleOnClick}
    >
      <Illustration width={42} height={42} />
      {isBigScreen ? (
        <BodyCopy noMargin>
          <FormattedMessage id={A11Y_LABEL[variant]} />
        </BodyCopy>
      ) : null}
    </Button>
  );
};

const Button = styled.button`
  display: flex;
  flex-direction: column;
  gap: ${spaceS};
  cursor: pointer;

  svg {
    transition: transform ${motionTimeXXS} ${motionEasingStandard};
  }

  &:hover svg {
    transform: scale(1.1);
  }

  /* reset button styles */
  border: none;
  padding: 0;
  background: transparent;
`;
